/**
 * DisabledTooltipButton — §3h "state-driven visibility with reasons" helper.
 *
 * The pattern: when a button is unavailable due to a recoverable state
 * (e.g. "Save or Discard your edits first" / "Enter Edit mode"), we keep
 * the button visible but disabled and show a tooltip on hover explaining
 * why. Hidden-when-unavailable is NOT the §3h doctrine — it leaves users
 * wondering where the affordance went.
 *
 * Usage:
 *
 *     <DisabledTooltipButton
 *       disabledReason={editMode ? null : "Enter Edit mode to detach"}
 *       onClick={handleDetach}
 *       size="xs"
 *       variant="subtle"
 *       color="red"
 *       leftSection={<IconUnlink size={14} />}
 *     >
 *       Detach
 *     </DisabledTooltipButton>
 *
 * When `disabledReason` is null/undefined/empty: enabled button, no
 * Tooltip wrapper. When `disabledReason` is a non-empty string: disabled
 * Button wrapped in Mantine Tooltip; the tooltip shows the reason on
 * hover/focus. The wrapping `<span>` ensures the Tooltip fires even when
 * the inner Button is disabled (a true HTML `disabled` button does not
 * dispatch hover events on its own in most browsers).
 *
 * Reference: CLAUDE.md §3h corollary 3 ("State-driven visibility, with
 * reasons"). Added 2026-06-09 during Task 5 Phase A1.
 */
import { Button, type ButtonProps, Tooltip, type TooltipProps } from '@mantine/core';
import * as React from 'react';

interface Props extends Omit<ButtonProps, 'disabled' | 'onClick'> {
  /**
   * If provided as a non-empty string, the button is rendered DISABLED and
   * wrapped in a Tooltip showing this reason on hover/focus.
   * If null/undefined/empty string, the button is enabled with no Tooltip
   * wrapper.
   */
  disabledReason?: string | null;
  /**
   * If provided AND the button is enabled (no disabledReason), wraps the
   * enabled button in a Tooltip showing this label on hover/focus. Use to
   * explain what a consequential action does (e.g. "Deletes this record")
   * so it can't be confused with a narrower affordance like a row-level
   * sever icon. Ignored when the button is disabled (disabledReason wins).
   */
  enabledTooltip?: string | null;
  /** Optional Tooltip placement override (default: 'top'). */
  tooltipPosition?: TooltipProps['position'];
  /** Click handler — only fires when the button is enabled. */
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  /** Button label (text and/or leftSection / rightSection icons). */
  children: React.ReactNode;
}

export function DisabledTooltipButton({
  disabledReason,
  enabledTooltip,
  tooltipPosition = 'top',
  onClick,
  children,
  ...buttonProps
}: Props) {
  // Enabled path — render Button; wrap in a Tooltip if an enabledTooltip was
  // provided (e.g. to explain a consequential action's blast radius).
  if (!disabledReason) {
    const btn = (
      <Button onClick={onClick} {...buttonProps}>
        {children}
      </Button>
    );
    if (enabledTooltip) {
      return (
        <Tooltip label={enabledTooltip} position={tooltipPosition} withArrow>
          {btn}
        </Tooltip>
      );
    }
    return btn;
  }

  // Disabled path — wrap in <span> so Tooltip's hover/focus events fire
  // even though the inner Button is HTML-disabled. The span has
  // display:inline-block so it sizes to the button (default span is
  // inline and would not capture the full button area).
  return (
    <Tooltip label={disabledReason} position={tooltipPosition} withArrow>
      <span style={{ display: 'inline-block' }}>
        <Button {...buttonProps} disabled>
          {children}
        </Button>
      </span>
    </Tooltip>
  );
}
